/**
 * Lorem-ipsum / 中文占位文本生成器。
 *
 * Deliberately decoupled from CSPRNG (`crypto.getRandomValues`):
 * the bytes are display copy for designers, not security tokens.
 * Using `Math.random()` keeps the bundle 0-byte-bigger and avoids
 * a Web Crypto roundtrip per word.
 *
 * Two language modes:
 *   - `'la'`: standard Latin lorem corpus, capitalised + punctuated
 *     into sentences and paragraphs the way every print mockup
 *     library does it.
 *   - `'zh'`: 4-character Chinese idioms pool. Output reads like
 *     filler copy ("龙腾虎跃，海阔天空") which previews fonts and
 *     typography without bringing real meaning into the mockup.
 *
 * Tests live in `tests/web/loremTool.test.ts`.
 */

/* ------------------------------------------------------------------ */
/* Word banks                                                          */
/* ------------------------------------------------------------------ */

/**
 * Classic lorem-ipsum corpus (200+ words). The "Lorem ipsum dolor sit
 * amet…" prefix is reserved for the very first sentence — every other
 * sentence draws words from the full pool.
 */
const LATIN_WORDS: ReadonlyArray<string> = [
  'lorem', 'ipsum', 'dolor', 'sit', 'amet', 'consectetur', 'adipiscing', 'elit',
  'sed', 'do', 'eiusmod', 'tempor', 'incididunt', 'ut', 'labore', 'et',
  'dolore', 'magna', 'aliqua', 'enim', 'ad', 'minim', 'veniam', 'quis',
  'nostrud', 'exercitation', 'ullamco', 'laboris', 'nisi', 'aliquip', 'ex',
  'ea', 'commodo', 'consequat', 'duis', 'aute', 'irure', 'in', 'reprehenderit',
  'voluptate', 'velit', 'esse', 'cillum', 'fugiat', 'nulla', 'pariatur',
  'excepteur', 'sint', 'occaecat', 'cupidatat', 'non', 'proident', 'sunt',
  'culpa', 'qui', 'officia', 'deserunt', 'mollit', 'anim', 'id', 'est',
  'laborum', 'curabitur', 'pretium', 'tincidunt', 'lacus', 'nulla', 'gravida',
  'orci', 'a', 'odio', 'nullam', 'varius', 'turpis', 'imperdiet', 'porttitor',
  'sapien', 'eget', 'lacinia', 'cursus', 'tortor', 'mauris', 'feugiat',
  'aliquet', 'tellus', 'rutrum', 'ligula', 'vulputate', 'nibh', 'donec',
  'augue', 'placerat', 'fermentum', 'class', 'aptent', 'taciti', 'sociosqu',
  'litora', 'torquent', 'per', 'conubia', 'nostra', 'inceptos', 'himenaeos',
  'praesent', 'tristique', 'magna', 'sodales', 'iaculis', 'massa', 'fringilla',
  'maecenas', 'porta', 'ante', 'sollicitudin', 'cras', 'pellentesque', 'libero',
  'arcu', 'nec', 'tempus', 'fusce', 'eros', 'rhoncus', 'venenatis', 'leo',
  'auctor', 'urna', 'integer', 'condimentum', 'finibus', 'felis', 'eu',
  'sagittis', 'morbi', 'ornare', 'molestie', 'mattis', 'lobortis', 'quam',
  'malesuada', 'metus', 'pharetra', 'congue', 'erat', 'fames', 'ac', 'turpis',
  'egestas', 'ridiculus', 'mus', 'natoque', 'penatibus', 'parturient', 'montes',
  'nascetur', 'platea', 'dictumst', 'dapibus', 'risus', 'commodo', 'vitae',
  'nunc', 'phasellus', 'consequat', 'mi', 'porttitor', 'iaculis', 'vestibulum',
  'maximus', 'scelerisque', 'volutpat', 'blandit', 'aliquam', 'sagittis',
  'imperdiet', 'pulvinar', 'lectus', 'suspendisse', 'potenti', 'rutrum',
  'fringilla', 'eleifend', 'purus', 'dignissim', 'cubilia', 'curae', 'taciti',
  'pellentesque', 'habitant', 'senectus', 'netus', 'tortor', 'placerat',
  'malesuada', 'auctor', 'praesent', 'sagittis', 'tristique', 'rhoncus',
  'volutpat', 'ridiculus',
];

/** Stable opening phrase reused as the first sentence of paragraph 1. */
const LATIN_OPENING =
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.';

/**
 * 200 + 中文四字短语 / 成语，去重后用作 zh 模式的字源。逗号分隔成
 * 「句」，句号收尾成「段」。词条整体偏中性 / 视觉化，避免带强情感
 * 倾向（不要让设计稿里突然出现"鸡飞狗跳"）。
 */
const CHINESE_IDIOMS: ReadonlyArray<string> = [
  '春暖花开', '海阔天空', '风和日丽', '青山绿水', '柳暗花明', '风平浪静',
  '万籁俱寂', '云淡风轻', '一望无际', '繁花似锦', '鸟语花香', '碧波荡漾',
  '秋高气爽', '银装素裹', '日新月异', '万象更新', '生机勃勃', '生生不息',
  '行云流水', '巧夺天工', '匠心独运', '一气呵成', '别具一格', '独树一帜',
  '相得益彰', '美轮美奂', '错落有致', '精雕细琢', '炉火纯青', '登峰造极',
  '日积月累', '水滴石穿', '锲而不舍', '坚持不懈', '勤能补拙', '熟能生巧',
  '精益求精', '一丝不苟', '兢兢业业', '尽善尽美', '深思熟虑', '集思广益',
  '有条不紊', '井然有序', '一目了然', '简明扼要', '深入浅出', '触类旁通',
  '举一反三', '融会贯通', '海纳百川', '兼收并蓄', '博采众长', '取长补短',
  '相辅相成', '并行不悖', '不谋而合', '殊途同归', '异曲同工', '同舟共济',
  '齐心协力', '众志成城', '万众一心', '同心同德', '志同道合', '不期而遇',
  '一拍即合', '相见恨晚', '灯火阑珊', '皓月当空', '繁星点点', '月明星稀',
  '清风明月', '春华秋实', '风花雪月', '良辰美景', '光阴似箭', '岁月如歌',
  '风雨同舟', '同甘共苦', '披荆斩棘', '风雨无阻', '砥砺前行', '勇往直前',
  '步步为营', '循序渐进', '稳扎稳打', '有的放矢', '一鼓作气', '势如破竹',
  '锐不可当', '一往无前', '无坚不摧', '攻无不克', '战无不胜', '所向披靡',
  '气吞山河', '波澜壮阔', '气势磅礴', '叱咤风云', '鼎力相助', '不遗余力',
  '殚精竭虑', '废寝忘食', '夙兴夜寐', '通宵达旦', '日理万机', '不辞辛劳',
  '任重道远', '高瞻远瞩', '运筹帷幄', '决胜千里', '审时度势', '随机应变',
  '通权达变', '灵活机动', '当机立断', '临机决断', '雷厉风行', '快刀斩麻',
  '大刀阔斧', '披星戴月', '昼夜兼程', '马不停蹄', '风驰电掣', '一日千里',
  '前程似锦', '前途无量', '蒸蒸日上', '欣欣向荣', '日趋繁荣', '繁荣昌盛',
  '国泰民安', '风调雨顺', '物阜民丰', '安居乐业', '太平盛世', '丰衣足食',
  '锦上添花', '雪中送炭', '众星捧月', '万古长青', '源远流长', '历久弥新',
  '推陈出新', '革故鼎新', '与时俱进', '继往开来', '承前启后', '薪火相传',
  '历经沧桑', '岁月静好', '一帆风顺', '吉祥如意', '心旷神怡', '怡然自得',
  '其乐融融', '欢声笑语', '喜气洋洋', '津津有味', '兴致勃勃', '神采奕奕',
  '容光焕发', '红光满面', '神清气爽', '心平气和', '从容不迫', '气定神闲',
  '泰然自若', '稳如泰山', '坚如磐石', '固若金汤', '安如磐石', '有备无患',
  '未雨绸缪', '居安思危', '防患未然', '因势利导', '顺水推舟', '水到渠成',
  '瓜熟蒂落', '驾轻就熟', '游刃有余', '左右逢源', '如鱼得水', '如虎添翼',
  '如日中天', '蒸蒸日上', '风生水起', '风起云涌', '波澜不惊', '风轻云淡',
  '碧海蓝天', '山清水秀', '湖光山色', '世外桃源', '人杰地灵', '物华天宝',
  '钟灵毓秀', '人文荟萃', '群贤毕至', '名满天下', '德高望重', '声名远扬',
];

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */

export type LoremKind = 'word' | 'sentence' | 'paragraph';
export type LoremLang = 'la' | 'zh';

export interface LoremOptions {
  kind: LoremKind;
  /** Number of units to generate. Clamped to [1, 200]. */
  count: number;
  lang: LoremLang;
  /**
   * When true (default for `kind === 'paragraph'`), prepend the
   * canonical "Lorem ipsum dolor sit amet…" opening to the very first
   * sentence. Has no effect for the `'zh'` corpus.
   */
  startWithLorem?: boolean;
}

/**
 * Generate filler text. Output shape:
 *   - `kind: 'word'`     → space-separated words ("foo bar baz")
 *   - `kind: 'sentence'` → "Foo bar baz. Qux quux. ..."
 *                          (sentences joined by single space)
 *   - `kind: 'paragraph'`→ paragraphs separated by `\n\n`
 *
 * Always returns a non-empty string for any `count >= 1`.
 */
export function genLorem(opts: LoremOptions): string {
  const count = clamp(opts.count, 1, 200);
  const startWithLorem = opts.startWithLorem ?? opts.kind === 'paragraph';

  switch (opts.kind) {
    case 'word':
      return generateWords(count, opts.lang);
    case 'sentence':
      return generateSentences(count, opts.lang, startWithLorem);
    case 'paragraph':
      return generateParagraphs(count, opts.lang, startWithLorem);
    default: {
      const _exhaustive: never = opts.kind;
      throw new Error(`unknown kind: ${String(_exhaustive)}`);
    }
  }
}

/* ------------------------------------------------------------------ */
/* Internals                                                           */
/* ------------------------------------------------------------------ */

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, Math.floor(n)));
}

/** Random integer in [min, max] (inclusive). */
function randInt(min: number, max: number): number {
  return min + Math.floor(Math.random() * (max - min + 1));
}

/** Pick a random element. Caller guarantees the array is non-empty. */
function pick<T>(xs: ReadonlyArray<T>): T {
  return xs[randInt(0, xs.length - 1)];
}

function generateWords(count: number, lang: LoremLang): string {
  if (lang === 'zh') {
    /* 中文按字符切，连续两个相同短语会显得很假，所以做一次去重 */
    const out: string[] = [];
    let prev = '';
    while (out.length < count) {
      const phrase = pick(CHINESE_IDIOMS);
      if (phrase === prev) continue;
      out.push(phrase);
      prev = phrase;
    }
    /* 4-char idioms 拼起来不带分隔，紧贴在一起；用户拿来看字距 / 字
     * 重时 visually denser 才像真实段落 */
    return out.join('');
  }
  const out: string[] = [];
  for (let i = 0; i < count; i++) out.push(pick(LATIN_WORDS));
  return out.join(' ');
}

function generateSentences(
  count: number,
  lang: LoremLang,
  startWithLorem: boolean,
): string {
  const sentences: string[] = [];
  for (let i = 0; i < count; i++) {
    if (i === 0 && startWithLorem && lang === 'la') {
      sentences.push(LATIN_OPENING);
    } else {
      sentences.push(buildSentence(lang));
    }
  }
  return sentences.join(lang === 'zh' ? '' : ' ');
}

function generateParagraphs(
  count: number,
  lang: LoremLang,
  startWithLorem: boolean,
): string {
  const paragraphs: string[] = [];
  for (let i = 0; i < count; i++) {
    /* 3-7 句一段：均匀分布，下限避开 1 句段（看起来像废话）*/
    const sentenceCount = randInt(3, 7);
    paragraphs.push(
      generateSentences(sentenceCount, lang, startWithLorem && i === 0),
    );
  }
  return paragraphs.join('\n\n');
}

function buildSentence(lang: LoremLang): string {
  if (lang === 'zh') {
    /* 一句 = 2-5 个 4-char 短语 + 句号 */
    const phraseCount = randInt(2, 5);
    const phrases: string[] = [];
    let prev = '';
    while (phrases.length < phraseCount) {
      const p = pick(CHINESE_IDIOMS);
      if (p === prev) continue;
      phrases.push(p);
      prev = p;
    }
    return phrases.join('，') + '。';
  }
  /* Latin: 6-14 词，首字母大写，末尾句号 */
  const len = randInt(6, 14);
  const words: string[] = [];
  for (let i = 0; i < len; i++) words.push(pick(LATIN_WORDS));
  const head = words[0];
  words[0] = head.charAt(0).toUpperCase() + head.slice(1);
  return `${words.join(' ')}.`;
}

/* ------------------------------------------------------------------ */
/* Test-only escape hatches                                            */
/* ------------------------------------------------------------------ */

/** @internal — exposed so tests can assert sentence-count without RNG noise. */
export const __corpusForTests = {
  LATIN_WORDS,
  CHINESE_IDIOMS,
  LATIN_OPENING,
};
